import { CellContext, Table } from '@tanstack/react-table';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import Button from '../../../components/Button/Button';
import { ButtonVariants } from '../../../components/Button/Button.types';
import Header from '../../../components/Header/Header';
import Link from '../../../components/Link/Link';
import SummaryBlock from '../../../components/SummaryBlock/SummaryBlock';
import { SummaryBlockVariants } from '../../../components/SummaryBlock/SummaryBlock.types';
import TableCard from '../../../components/Tables/TableCard/TableCard';
import Tag from '../../../components/Tag/Tag';
import { TagTypes } from '../../../components/Tag/Tag.types';
import { useActiveGlobalFreezes } from '../../../services/freeze/freeze.service';
import { useSubmissions } from '../../../services/submissions/submissions.service';
import {
  FzSubmissionSummary,
  Submission,
  SubmissionStatus,
  SubmissionStatusLabels,
  SubmissionSubType,
  SubmissionSubTypeLabels,
} from '../../../types/submission.types';
import useFranchiseeAlias from '../../../utils/useFranchiseeAlias/useFranchiseeAlias';
import styles from './Submissions.module.css';
import { useTranslation } from 'react-i18next';

const TagCell = ({ row }: CellContext<Submission, unknown>) => {
  const { t } = useTranslation();
  const labelType: 'Pending' | 'Completed' = SubmissionStatusLabels[row.original.status];
  const label = t(`submissionStatusLabels.${row.original.status}`) as string;
  return <Tag label={label} type={TagTypes[labelType]} />;
};

const LinkCell = ({ row }: CellContext<Submission, unknown>) => (
  <Link to={`/submissions/${row.original.id}`}>Details</Link>
);

export const summarizeSubmissions = (data: Submission[]) =>
  data.reduce<FzSubmissionSummary>(
    (previousValue: FzSubmissionSummary, currentValue: Submission) => {
      const summaries = { ...previousValue };
      if (currentValue.status === SubmissionStatus.PENDING) {
        switch (currentValue.subType) {
          case SubmissionSubType.BUNDLE_SETTING_CHANGE:
            summaries.pendingBundleSettingCount += 1;
            break;
          case SubmissionSubType.DEACTIVATE_BUNDLE:
            summaries.pendingDeactivateCount += 1;
            break;
          case SubmissionSubType.NEW_BUNDLE_GROUP:
            summaries.pendingNewGroupCount += 1;
            break;
          case SubmissionSubType.EDIT_BUNDLE_GROUP:
            summaries.pendingEditGroupCount += 1;
            break;
          default:
            break;
        }
      } else {
        switch (currentValue.subType) {
          case SubmissionSubType.BUNDLE_SETTING_CHANGE:
            summaries.completedBundleSettingCount += 1;
            break;
          case SubmissionSubType.DEACTIVATE_BUNDLE:
            summaries.completedDeactivateCount += 1;
            break;
          case SubmissionSubType.NEW_BUNDLE_GROUP:
            summaries.completedNewGroupCount += 1;
            break;
          case SubmissionSubType.EDIT_BUNDLE_GROUP:
            summaries.completedEditGroupCount += 1;
            break;
          default:
            break;
        }
      }
      return summaries;
    },
    {
      pendingBundleSettingCount: 0,
      pendingDeactivateCount: 0,
      pendingNewGroupCount: 0,
      pendingEditGroupCount: 0,
      completedBundleSettingCount: 0,
      completedDeactivateCount: 0,
      completedNewGroupCount: 0,
      completedEditGroupCount: 0,
    },
  );

const Submissions: FC = () => {
  const { t } = useTranslation();
  const { alias } = useFranchiseeAlias();
  const { data: activeGlobalFreezeData } = useActiveGlobalFreezes(true);
  const freezeExists = activeGlobalFreezeData?.data && activeGlobalFreezeData?.data?.length > 0;
  const { data, isLoading } = useSubmissions(true);
  const [tableApi, setTableApi] = useState<Table<Submission> | null>(null);
  const submissions = data?.data || [];
  const {
    pendingBundleSettingCount,
    pendingDeactivateCount,
    pendingNewGroupCount,
    pendingEditGroupCount,
    completedBundleSettingCount,
    completedDeactivateCount,
    completedNewGroupCount,
    completedEditGroupCount,
  } = summarizeSubmissions(submissions);

  const clearFilters = () => {
    if (tableApi) tableApi.resetColumnFilters();
  };

  const accessTableApi = (apiParam: Table<Submission>) => {
    setTableApi(apiParam);
  };

  return (
    <>
      <Header title={t('submissions.title')} />
      <div className={styles.summaryBlocksMobile} data-testid="summaryBlocksMobile">
        <SummaryBlock
          variant={SummaryBlockVariants.LightBlue}
          blocks={[
            {
              label: '',
              count: pendingBundleSettingCount + pendingDeactivateCount + pendingNewGroupCount + pendingEditGroupCount,
            },
          ]}
          title={t('submissions.pending.title')}
          tooltipText={t('submissions.pending.tooltip')}
        />
        <SummaryBlock
          variant={SummaryBlockVariants.Green}
          blocks={[
            {
              label: '',
              count:
                completedBundleSettingCount +
                completedDeactivateCount +
                completedNewGroupCount +
                completedEditGroupCount,
            },
          ]}
          title={t('submissions.completed.title')}
          tooltipText={t('submissions.completed.tooltip')}
        />
      </div>
      <div className={styles.summaryBlocks} data-testid="summaryBlocks">
        <SummaryBlock
          variant={SummaryBlockVariants.LightBlue}
          blocks={[
            { label: t('submissions.pending.bundleSettingChange'), count: pendingBundleSettingCount },
            { label: t('submissions.pending.deactivateBundle'), count: pendingDeactivateCount },
            { label: t('submissions.pending.newStoreGroup'), count: pendingNewGroupCount },
            { label: t('submissions.pending.editStoreGroup'), count: pendingEditGroupCount },
          ]}
          title={t('submissions.pending.title')}
          tooltipText={t('submissions.pending.tooltip')}
        />
        <SummaryBlock
          variant={SummaryBlockVariants.Green}
          blocks={[
            { label: t('submissions.completed.bundleSettingChange'), count: completedBundleSettingCount },
            { label: t('submissions.completed.deactivateBundle'), count: completedDeactivateCount },
            { label: t('submissions.completed.newStoreGroup'), count: completedNewGroupCount },
            { label: t('submissions.completed.editStoreGroup'), count: completedEditGroupCount },
          ]}
          title={t('submissions.completed.title')}
          tooltipText={t('submissions.completed.tooltip')}
        />
      </div>
      <div
        className={classNames(styles.table, {
          [styles.withAlias]: alias !== null,
          [styles.withFreeze]: freezeExists,
        })}
      >
        <div className={styles.tableContainer}>
          <div className={styles.tableLabel}>
            <h2>{t('submissions.sortFilterLabel')}</h2>
            <Button variant={ButtonVariants.Text} onClick={clearFilters}>
              {t('submissions.clearFiltersButton')}
            </Button>
          </div>
          <TableCard
            accessTableApi={accessTableApi}
            className={styles.tableCard}
            includeMinWidth={false}
            data={submissions}
            enableFilters
            columns={[
              {
                header: t('submissions.table.submissionDate'),
                accessorFn: ({ createdDate }) =>
                  DateTime.fromISO(createdDate, { zone: 'UTC' }).setZone('local').toFormat('f'),
                enableSorting: true,
              },
              {
                header: t('submissions.table.completedDate'),
                accessorFn: ({ completedDate }) =>
                  completedDate ? DateTime.fromISO(completedDate, { zone: 'UTC' }).setZone('local').toFormat('f') : '-',
                enableSorting: true,
              },
              {
                header: t('submissions.table.requestedBy'),
                accessorFn: ({ createdBy }) => createdBy || '-',
                enableSorting: true,
              },
              {
                header: t('submissions.table.action'),
                accessorFn: ({ subType }) => SubmissionSubTypeLabels[subType as SubmissionSubType],
                enableSorting: true,
              },
              {
                header: t('submissions.table.storeGroupsCount'),
                accessorKey: 'bundleGroupsAffected',
                enableSorting: true,
              },
              {
                header: t('submissions.table.status'),
                accessorKey: 'status',
                cell: TagCell,
                enableSorting: true,
              },
              {
                header: '',
                accessorKey: 'id',
                enableColumnFilter: false,
                cell: LinkCell,
              },
            ]}
            isLoading={isLoading}
            returnedRecordsText={(count) => t('submissions.table.returnedRecord', { count })}
          />
        </div>
      </div>
    </>
  );
};

export default Submissions;
